import { BCMSCurrency, BCMSDate, BCMSRecord, DisabledPrompt, Link, useRoute, useWorkflowStep } from "@bcm-dev/bcms-vite";
import { getDefaultPayment, getWorkflowSteps } from "compid/workflows/MakeAPayment";
import { Account, Autopay, Bill, Biller, Payment } from "model";

import ButtonContainer from "compid/shared/containers/ButtonContainer";
import PageLayoutContainer from "compid/shared/containers/PageLayoutContainer";
import CenterColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer";
import PageView from "compid/shared/containers/PageViewContainer";
import BillerView from "compid/shared/views/BillerView";
import AutopayPromoView from "compid/shared/views/PromoView/components/views/AutopayPromoView";
import PromoeBillsView from "compid/shared/views/PromoView/components/views/PromoeBillsView";
import TableView, { TableRowView } from "compid/shared/views/TableView";
import UIButton from "compid/ui/UIButton";
import UITextArea from "compid/ui/UITextArea";
import { SVGAsset } from "res";
import "./styles.css";

export default function PaymentConfirmationPage(props) {
    const workflowStep = useWorkflowStep(getDefaultPayment, getWorkflowSteps);
    BCMSRecord.globalControllerFor([ Account, Biller, Bill, Payment, Autopay ], workflowStep.globalController);

    const { bill, payment, info } = workflowStep.getStepState;
    const { to: biller } = payment;

    const amount = payment.amount.format();
    const confirmation = payment.confirmationNumber ? payment.confirmationNumber : "1234567019";
    const account = payment.from.name + " " + payment.from.number;
    const date = payment.formatDate;
    // const deliveryMethod = payment.to.paymentType == "paper" ? "Check" : "Electronic";

    const isPaper = payment.to.paymentType === "paper";
    const isElectronic = payment.to.paymentType === "electronic";
    const isVirtualCard = payment.to.paymentType === "virtualCard";
    const isInstantPayment = payment.to.paymentType === "instantPayment";
    const isWithinMinutes = payment.to.paymentType === "withinMinutes";

    let message = `Your ${ amount } payment is scheduled for ${ date }`;

    if (isInstantPayment) { message = `Your payment of ${ amount } will be delivered today`; }
    if (isWithinMinutes) { message = `Your payment of ${ amount } has been delivered`; }
    
    // START: "Withdrawn" ui which can be affected by "Real time good funds" branding.
    const paymentDate = payment.date;
    const withdrawnOnDate = payment.withdrawnOnDate;
    let withdrawnOnDateFormatted = withdrawnOnDate ? withdrawnOnDate.format("M d") : paymentDate.format("M d");
    const hasRealTimeGoodFunds = Payment.hasRealTimeGoodFunds(payment.to.paymentType, workflowStep.model.branding);
   
    // END: "Withdrawn" ui which can be affected by "Real time good funds" branding.
    const deliveryMethod = payment.to.paymentType == "paper" ? hasRealTimeGoodFunds ? "Check" : "Overnight Check" : "Electronic";
    const { options } = workflowStep.model.branding ?? {};
    const showOvernightDelivery = options.overnight_check_rxp22 === "true" && options.paper_biller_rush_delivery_rxp22 === "Overnight Check" && payment.hasFee;

    const navigate = useRoute();
    let showAutoPromo = bill.biller.eBillStatus === "Active" && !(bill.biller.autopay instanceof Autopay);
    let showEbillPromo = bill.biller.paymentType === "electronic" && bill.biller.eBillStatus === "Not Enrolled";
    if(isVirtualCard && !(bill.biller.autopay instanceof Autopay)) showAutoPromo = true;

    const feeAmount = payment.feeAmount.format();
    const totalAmount = BCMSCurrency.init(parseFloat(payment.amount.amount) + parseFloat(payment.feeAmount.amount ?? "0"));

    //const billerAddress = biller.address;
    //let billerCityState = `${ biller.city }, ${ biller.state }, ${ biller.zip }`;

    const billerAddress = info.address.length === 0 ? biller.address : info.address;
    const billerCity = info.city.length === 0 ? biller.city : info.city;
    const billerState = info.state.length === 0 ? biller.state : info.state;
    const billerZip = info.zipFive.length === 0 ? biller.zip : info.zipFive;

    let billerCityState = `${ billerCity }, ${ billerState }, ${ billerZip }`;
    billerCityState = billerCityState.replace(/[, ]/g, "").length === 0 ? "" : billerCityState;
    
    const estimatedDeliveryMessage = `The timing of the withdrawal depends on when the check is cashed. This may happen on, before, or after ${ date }.`;
    const withdrawnOnView = <TableRowView rowClassName="withdraw-on-row" label="Withdraw on" data={ withdrawnOnDateFormatted } dataPoint="Withdraw_On_Date" />;
    const withdrawOnFinePrint = `If funds are not available on that date, the payment will fail.`;
    let showWithdrawnOnView = false;
    let showWithdrawnOnFinePrintView = false;

    // Block for determining when the withdraw fine print message is shown.
    if(isElectronic && hasRealTimeGoodFunds) {
        showWithdrawnOnView = true;
        const todaysDate = BCMSDate.init();

        // Show the withdraw fine print if the withdraw date is not immediate.
        if(withdrawnOnDate.isAfter(todaysDate)) {
            showWithdrawnOnFinePrintView = true;
        }
    }
    
    const withdrawOnFinePrintView = <TableRowView label="" data={ withdrawOnFinePrint } dataClass="fine-print" />;

    const submit = evt => {
        if(payment.hasFee) {
            workflowStep.update([ "payment", "amount" ], totalAmount);
            workflowStep.update([ "bill", "payment" ], payment);
        }

        BCMSRecord.batch([
            Payment.update(payment),
            Bill.update(bill)
        ], workflowStep.globalController);

        navigate("/PayBills");
    };

    return (
        <PageView className="payment-confirmation-page">
            <p className="page-sub-header semi-bold-text flex-container flex-horizontal">
                <span className="flex-width" data-point="Payment_Confirmation_Header">PAYMENT CONFIRMATION</span>

                <DisabledPrompt>
                    <Link to="/" className="flex-container flex-horizontal align-center set-width link print-link">
                        <SVGAsset id="PrinterIcon" className="left-icon" />
                        <p data-point="Payment_Confirmation_Print">Print</p>
                    </Link>
                </DisabledPrompt>
            </p>

            <PageLayoutContainer>
                <div className="flex-container flex-vertical flex-width">
                    <div className="flex-height flex-container flex-vertical upper-content-container">
                        { showAutoPromo && <AutopayPromoView onClick={ () => navigate(`/addAutoPayWorkflow/${ bill.biller.id }`) } /> }
                        { showEbillPromo && <PromoeBillsView onClick={ () => navigate(`/requestEBillWorkflow/${ bill.biller.id }`) } /> }


                        <BillerView biller={ payment.to } />
                    </div>
                    
                    <div className="flex-height flex-width flex-container all-center">
                        <CenterColumnContainer>
                            <div className="form-group">
                                <div className="flex-container flex-vertical all-center form-row">
                                    <SVGAsset id="CheckmarkIcon" className="green-icon" />
                                    <p className="gray-text" data-point="Confirmation_Message">{ message }</p>
                                </div>
                                <div className="form-row">
                                    <TableView>
                                        <TableRowView label="Confirmation" data={ confirmation } />
                                        <TableRowView rowClassName={ payment.hasFee ? "amount-row" : "" } label="Amount" data={ amount } />

                                        {   payment.hasFee &&
                                            <>
                                                <TableRowView rowClassName="amount-row" label="Delivery fee" data={ feeAmount } />
                                                <TableRowView label={<b>Total amount</b>} data={<b>{ totalAmount.format() }</b>} dataPoint="Payment_Confirmation_Total" />
                                            </> }
                                 
                                        <TableRowView label="Pay From" data={ account } />
                                        
                                        { !isPaper && <TableRowView label="Deliver by" data={ date } /> }
                                        { isPaper && 
                                        <>
                                            <TableRowView label="Estimated delivery" data={ date } />
                                            { !hasRealTimeGoodFunds && <TableRowView label="" data={ estimatedDeliveryMessage } dataClass="fine-print" /> }
                                            { hasRealTimeGoodFunds && <TableRowView label="Withdraw on" data={  withdrawnOnDateFormatted  } dataClass="fine-print" dataPoint="Fine_Print_Withdraw_On_Date" /> }
                                        </> 
                                        }

                                        { showWithdrawnOnView && withdrawnOnView }
                                        { showWithdrawnOnFinePrintView && withdrawOnFinePrintView }
                                        <TableRowView label="Delivery method" data={  deliveryMethod } />
                                        
                                        { ((payment.hasFee && biller.paymentType === "paper") || (biller.paymentType === "paper" && showOvernightDelivery)) && 
                                            <TableRowView rowClassName="overnight-address" label="Overnight address" data={<><p>{ billerAddress }</p><p>{ billerCityState }</p></>} /> }

                                    </TableView>
                                </div>

                                {
                                    isVirtualCard &&
                                    <div className="form-row flex-height">
                                        <div className="flex-container flex-horizontal align-center">
                                            <span className="sub-text">{ Biller.getVirtualCardDisclaimer(true) }</span>
                                        </div>
                                    </div>
                                }

                                <div className="form-row">
                                    <UITextArea dataPoint="Payment_Confirmation_Page"/>
                                </div>

                                <ButtonContainer className="form-button-container">
                                    <UIButton onClick={ submit } className="primary-button" dataPoint="Payment_Confirmation_Done">Done</UIButton>
                                </ButtonContainer>
                                </div>
                            </CenterColumnContainer>
                    </div>
                </div>
            </PageLayoutContainer>
        </PageView>
    );
}